import { useEffect, useMemo, useRef, useState } from "react";
import { heroLook, HERO_SLOTS, type EmoteKind } from "../../shared/avatar";
import type { Framing } from "../char3d/frame";
import { specFromEquipped } from "../char3d/types";

/**
 * Avatars. The figure itself is a 3D portrait (src/client/char3d – photographed in a studio, cached, shown as a picture); until the 3D chunk
 * has loaded – or for good if WebGL is not available – the original flat SVG figure is drawn from the same `data`. Backgrounds, frames and
 * banners are always 2D. Nothing is copied from any game.
 */
type Item = { id: string; data: Record<string, any>; name?: string } | undefined;
export type Equipped = Partial<Record<"hero" | "skin" | "outfit" | "hat" | "accessory" | "back" | "hand" | "pet" | "aura" | "background" | "frame" | "banner" | "title" | "emote" | "cardback" | "boardtheme", Item>>;

/** the flat SVG fallback only knows a handful of shapes – every species is mapped onto the closest one */
const SHAPE_OF: Record<string, string> = {
  kid: "round", blob: "round", fox: "cat", dog: "cat", cat: "cat", bunny: "tiny", bear: "round", panda: "round", penguin: "tiny", owl: "round",
  dino: "dragon", dragon: "dragon", frog: "round", robot: "robot", alien: "alien",
};
const shapeOf = (d: Record<string, any>): string => (d.shape as string | undefined) ?? SHAPE_OF[d.species as string] ?? "round";

/** the 3D portrait module is loaded once, on first use */
let portraitMod: Promise<typeof import("../char3d/portrait") | null> | null = null;
const loadPortraits = () => (portraitMod ??= import("../char3d/portrait").then((m) => (m.portraitsSupported() ? m : null)).catch(() => null));
const BUCKETS = [96, 144, 192, 256, 384];
const bucketFor = (size: number) => {
  const want = size * Math.min(typeof devicePixelRatio === "number" ? devicePixelRatio : 1, 2);
  return BUCKETS.find((b) => b >= want) ?? 384;
};

/** a 3D portrait of `equipped` – `null` while it is being taken (callers show the flat avatar or a skeleton meanwhile). `enabled=false` postpones the work (off-screen shop cards). */
export function usePortrait(equipped: Equipped | null | undefined, framing: Framing, size: number, opts: { enabled?: boolean; pose?: { kind: EmoteKind; at: number } } = {}): string | null {
  const spec = useMemo(() => specFromEquipped(equipped), [equipped]);
  const px = bucketFor(size);
  const enabled = opts.enabled !== false;
  const pose = opts.pose;
  const key = useMemo(() => `${framing}|${px}|${pose ? pose.kind + pose.at : ""}|${JSON.stringify(spec)}`, [spec, framing, px, pose?.kind, pose?.at]);
  const [shot, setShot] = useState<{ key: string; url: string } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    loadPortraits().then((m) => {
      if (!m || !live) return;
      const o = pose ? { pose } : {};
      const hit = m.cachedPortrait(spec, framing, px, o);
      if (hit) { setShot({ key, url: hit }); return; }
      m.renderPortrait(spec, framing, px, o).then((url) => { if (live && url) setShot({ key, url }); });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return shot && shot.key === key ? shot.url : null;
}

/** the figure's run cycle (side view, transparent frames side by side) – prepared quietly in the background so it is ready when somebody clicks.
 *  `null` while it is being made, and for good when WebGL is not available (the page transition then shows the flat figure instead). */
export function useRunSheet(equipped: Equipped | null | undefined, delayMs = 1200): { url: string; frames: number; px: number } | null {
  const spec = useMemo(() => specFromEquipped(equipped), [equipped]);
  const [sheet, setSheet] = useState<{ key: string; s: { url: string; frames: number; px: number } } | null>(null);
  const key = useMemo(() => JSON.stringify(spec), [spec]);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      loadPortraits().then((m) => {
        if (!m || !live) return;
        const hit = m.cachedRunSheet(spec);
        if (hit) { setSheet({ key, s: hit }); return; }
        m.renderRunSheet(spec).then((r) => { if (live && r) setSheet({ key, s: r }); });
      });
    }, delayMs);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return sheet && sheet.key === key ? sheet.s : null;
}

/** just the figure – no background tile, no frame (flat drawing; used where the 3D picture is not available) */
export function AvatarBare({ equipped, size = 160, className }: { equipped?: Equipped | null; size?: number; className?: string }) {
  const e = equipped ?? {};
  const skin = e.skin?.data ?? { shape: "round", body: "#4cc9f0", accent: "#1b2a6b" };
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" focusable="false" style={{ display: "block", overflow: "visible" }}>
      <Body shape={shapeOf(skin)} body={skin.body ?? "#4cc9f0"} accent={skin.accent ?? "#1b2a6b"} />
      {e.accessory && <Accessory d={e.accessory.data} />}
      {e.hat && <Hat d={e.hat.data} />}
    </svg>
  );
}

/** true once the element has been on screen (shop cards render their portrait lazily) */
export function useSeen<T extends Element>(): [{ current: T | null }, boolean] {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((e) => { if (e.some((x) => x.isIntersecting)) { setSeen(true); io.disconnect(); } }, { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

export const Pattern = ({ kind, id }: { kind: string; id: string }) => {
  if (kind === "stars")
    return (
      <g opacity=".55" fill="#fff">
        {[[12, 14], [80, 10], [30, 40], [88, 52], [60, 24], [18, 78], [72, 84], [48, 60]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 1.6 : 1} />
        ))}
      </g>
    );
  if (kind === "grid")
    return (
      <g stroke="#fff" strokeOpacity=".16" strokeWidth=".6">
        {[20, 40, 60, 80].map((n) => (
          <g key={n}>
            <line x1={n} y1="0" x2={n} y2="100" />
            <line x1="0" y1={n} x2="100" y2={n} />
          </g>
        ))}
      </g>
    );
  if (kind === "hex")
    return (
      <g fill="none" stroke="#fff" strokeOpacity=".2" strokeWidth=".8">
        {[[20, 22], [50, 22], [80, 22], [35, 48], [65, 48], [20, 74], [50, 74], [80, 74]].map(([x, y], i) => (
          <polygon key={i} points={`${x},${y - 9} ${x + 8},${y - 4.5} ${x + 8},${y + 4.5} ${x},${y + 9} ${x - 8},${y + 4.5} ${x - 8},${y - 4.5}`} />
        ))}
      </g>
    );
  if (kind === "waves")
    return (
      <g fill="none" stroke="#fff" strokeOpacity=".25" strokeWidth="1.2">
        {[30, 50, 70, 90].map((y) => (
          <path key={y} d={`M0 ${y} Q 12 ${y - 8} 25 ${y} T 50 ${y} T 75 ${y} T 100 ${y}`} />
        ))}
      </g>
    );
  if (kind === "rays")
    return (
      <g stroke="#fff" strokeOpacity=".2" strokeWidth="5" strokeLinecap="round">
        {[-4, -3, -2, -1, 0, 1, 2, 3, 4].map((i) => <line key={i} x1="50" y1="112" x2={50 + i * 28} y2="-14" />)}
      </g>
    );
  if (kind === "dots")
    return (
      <g fill="#fff" fillOpacity=".24">
        {[0, 1, 2, 3, 4].flatMap((r) => [0, 1, 2, 3, 4].map((q) => <circle key={`${r}-${q}`} cx={q * 24 + (r % 2 ? 12 : 0)} cy={r * 22 + 6} r="4.2" />))}
      </g>
    );
  if (kind === "diamonds")
    return (
      <g fill="none" stroke="#fff" strokeOpacity=".22" strokeWidth=".9">
        {[0, 1, 2, 3, 4].flatMap((r) => [0, 1, 2, 3, 4].map((q) => <polygon key={`${r}-${q}`} points={`${q * 25},${r * 25 - 11} ${q * 25 + 11},${r * 25} ${q * 25},${r * 25 + 11} ${q * 25 - 11},${r * 25}`} />))}
      </g>
    );
  if (kind === "bokeh")
    return (
      <g>
        {[[18, 24, 10, ".16"], [70, 18, 14, ".12"], [44, 52, 8, ".2"], [84, 60, 11, ".14"], [22, 76, 13, ".13"], [60, 84, 7, ".2"], [92, 90, 9, ".16"], [8, 50, 6, ".2"]].map(([x, y, r, o], i) => <circle key={i} cx={x as number} cy={y as number} r={r as number} fill="#fff" fillOpacity={o as string} />)}
      </g>
    );
  if (kind === "mountains")
    return (
      <g>
        <path d="M0 100 L0 66 L20 44 L38 62 L58 34 L82 60 L100 46 L100 100Z" fill="#000" fillOpacity=".2" />
        <path d="M0 100 L0 82 L18 66 L36 80 L56 60 L78 80 L100 68 L100 100Z" fill="#000" fillOpacity=".28" />
        <path d="M58 34 L52 44 L58 42 L63 46Z M20 44 L15 52 L20 50 L25 54Z" fill="#fff" fillOpacity=".7" />
      </g>
    );
  if (kind === "city")
    return (
      <g>
        {[[2, 58, 12], [16, 44, 14], [32, 64, 12], [46, 36, 16], [64, 56, 13], [79, 46, 14], [94, 66, 10]].map(([x, y, w], i) => (
          <g key={i}>
            <rect x={x} y={y} width={w} height={100 - (y as number)} fill="#000" fillOpacity=".4" />
            {[0, 1, 2, 3].map((k) => <rect key={k} x={(x as number) + 2.5 + (k % 2) * 5.5} y={(y as number) + 5 + Math.floor(k / 2) * 9} width="2.6" height="3.2" fill="#fde68a" fillOpacity={0.5 + ((i + k) % 3) * 0.2} />)}
          </g>
        ))}
      </g>
    );
  if (kind === "aurora")
    return (
      <g fill="none" strokeLinecap="round" style={{ mixBlendMode: "screen" }}>
        <path d="M-10 38 Q 22 6 50 36 T 110 26" stroke="#6ee7b7" strokeWidth="14" strokeOpacity=".25" />
        <path d="M-10 58 Q 26 30 54 56 T 110 46" stroke="#a78bfa" strokeWidth="12" strokeOpacity=".25" />
        <path d="M-10 76 Q 20 54 52 76 T 110 68" stroke="#38bdf8" strokeWidth="9" strokeOpacity=".2" />
      </g>
    );
  if (kind === "clouds")
    return (
      <g fill="#fff" fillOpacity=".4">
        {[[22, 30, 1], [72, 20, 0.8], [58, 66, 1.2], [14, 78, 0.8]].map(([x, y, k], i) => (
          <g key={i} transform={`translate(${x} ${y}) scale(${k})`}><circle cx="0" cy="0" r="8" /><circle cx="9" cy="-3" r="10" /><circle cx="19" cy="1" r="7.5" /><rect x="-8" y="0" width="34" height="8" rx="4" /></g>
        ))}
      </g>
    );
  void id;
  return null;
};

function Body({ shape, body, accent }: { shape: string; body: string; accent: string }) {
  const eyes = (y: number, gap = 9, r = 3.2) => (
    <g>
      <circle cx={50 - gap} cy={y} r={r} fill="#fff" />
      <circle cx={50 + gap} cy={y} r={r} fill="#fff" />
      <circle cx={50 - gap + 0.8} cy={y + 0.4} r={r / 2} fill={accent} />
      <circle cx={50 + gap + 0.8} cy={y + 0.4} r={r / 2} fill={accent} />
    </g>
  );
  const smile = (y: number, w = 7) => <path d={`M${50 - w} ${y} Q50 ${y + 7} ${50 + w} ${y}`} stroke={accent} strokeWidth="2.2" strokeLinecap="round" fill="none" />;
  switch (shape) {
    case "tiny":
      return (
        <g>
          <ellipse cx="50" cy="72" rx="20" ry="18" fill={body} />
          <circle cx="50" cy="52" r="22" fill={body} />
          {eyes(52, 8, 3.6)}
          {smile(62, 6)}
        </g>
      );
    case "tall":
      return (
        <g>
          <rect x="30" y="52" width="40" height="44" rx="16" fill={accent} opacity=".9" />
          <ellipse cx="50" cy="42" rx="22" ry="27" fill={body} />
          {eyes(40, 9, 3.2)}
          {smile(54)}
        </g>
      );
    case "robot":
      return (
        <g>
          <line x1="50" y1="14" x2="50" y2="24" stroke={accent} strokeWidth="3" />
          <circle cx="50" cy="12" r="4" fill={accent} />
          <rect x="26" y="26" width="48" height="40" rx="9" fill={body} />
          <rect x="34" y="68" width="32" height="24" rx="6" fill={accent} opacity=".85" />
          <rect x="33" y="36" width="12" height="9" rx="3" fill="#0b1030" />
          <rect x="55" y="36" width="12" height="9" rx="3" fill="#0b1030" />
          <circle cx="39" cy="40.5" r="2.6" fill="#4cc9f0" />
          <circle cx="61" cy="40.5" r="2.6" fill="#4cc9f0" />
          <rect x="38" y="52" width="24" height="5" rx="2.5" fill="#0b1030" />
        </g>
      );
    case "alien":
      return (
        <g>
          <ellipse cx="50" cy="76" rx="16" ry="15" fill={body} opacity=".9" />
          <path d="M50 18 C 80 18 84 56 66 66 C 58 71 42 71 34 66 C 16 56 20 18 50 18Z" fill={body} />
          <ellipse cx="38" cy="46" rx="7" ry="10" fill="#0b1030" transform="rotate(-18 38 46)" />
          <ellipse cx="62" cy="46" rx="7" ry="10" fill="#0b1030" transform="rotate(18 62 46)" />
          <circle cx="40" cy="43" r="2" fill="#fff" />
          <circle cx="60" cy="43" r="2" fill="#fff" />
          <path d="M44 62 Q50 66 56 62" stroke={accent} strokeWidth="2" fill="none" strokeLinecap="round" />
        </g>
      );
    case "knight":
      return (
        <g>
          <rect x="24" y="64" width="52" height="32" rx="10" fill={accent} />
          <path d="M26 56 V36 C26 22 38 16 50 16 C62 16 74 22 74 36 V56 Z" fill={body} />
          <rect x="32" y="38" width="36" height="9" rx="3" fill="#0b1030" />
          <line x1="50" y1="16" x2="50" y2="56" stroke={accent} strokeWidth="2.5" />
          <rect x="46" y="10" width="8" height="8" rx="2" fill="#ef4444" />
        </g>
      );
    case "astro":
      return (
        <g>
          <rect x="30" y="66" width="40" height="30" rx="12" fill={accent} />
          <circle cx="50" cy="42" r="28" fill={body} />
          <circle cx="50" cy="42" r="20" fill="#0b1030" />
          <ellipse cx="42" cy="36" rx="6" ry="3.4" fill="#fff" opacity=".35" transform="rotate(-20 42 36)" />
          {eyes(44, 7, 2.6)}
          {smile(52, 5)}
        </g>
      );
    case "cat":
      return (
        <g>
          <path d="M26 40 L30 12 L46 26 Z" fill={body} />
          <path d="M74 40 L70 12 L54 26 Z" fill={body} />
          <path d="M31 30 L33 20 L40 26 Z" fill={accent} opacity=".7" />
          <path d="M69 30 L67 20 L60 26 Z" fill={accent} opacity=".7" />
          <ellipse cx="50" cy="48" rx="26" ry="24" fill={body} />
          <ellipse cx="50" cy="82" rx="18" ry="14" fill={body} />
          <ellipse cx="40" cy="46" rx="3.2" ry="5" fill={accent} />
          <ellipse cx="60" cy="46" rx="3.2" ry="5" fill={accent} />
          <path d="M46 56 L50 59 L54 56 Z" fill={accent} />
          <path d="M50 59 V63 M50 63 Q45 67 41 64 M50 63 Q55 67 59 64" stroke={accent} strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </g>
      );
    case "dragon":
      return (
        <g>
          <path d="M20 54 L8 34 L30 44Z M80 54 L92 34 L70 44Z" fill={accent} />
          <path d="M34 26 L38 10 L46 24Z M66 26 L62 10 L54 24Z" fill={accent} />
          <ellipse cx="50" cy="48" rx="27" ry="26" fill={body} />
          <ellipse cx="50" cy="82" rx="19" ry="14" fill={body} />
          <circle cx="40" cy="42" r="4" fill="#fde047" />
          <circle cx="60" cy="42" r="4" fill="#fde047" />
          <rect x="39" y="38" width="2.4" height="8" rx="1.2" fill="#0b1030" />
          <rect x="59" y="38" width="2.4" height="8" rx="1.2" fill="#0b1030" />
          <circle cx="44" cy="58" r="1.6" fill={accent} />
          <circle cx="56" cy="58" r="1.6" fill={accent} />
        </g>
      );
    default:
      return (
        <g>
          <ellipse cx="50" cy="78" rx="24" ry="18" fill={accent} opacity=".9" />
          <circle cx="50" cy="46" r="30" fill={body} />
          {eyes(44, 10, 4)}
          {smile(58, 8)}
        </g>
      );
  }
}

function Hat({ d }: { d: Record<string, any> }) {
  const c = d.color ?? "#ef4444";
  const a = d.accent ?? "#fde047";
  switch (d.kind) {
    case "cap": return <g><path d="M24 34 Q50 4 76 34 Z" fill={c} /><rect x="22" y="32" width="46" height="6" rx="3" fill={c} /><rect x="60" y="32" width="26" height="5" rx="2.5" fill={c} /></g>;
    case "headband": return <rect x="22" y="30" width="56" height="7" rx="3.5" fill={c} />;
    case "beanie": return <g><path d="M22 36 Q50 -2 78 36 Z" fill={c} /><rect x="20" y="32" width="60" height="9" rx="4" fill={a} opacity=".8" /><circle cx="50" cy="6" r="5" fill={a} /></g>;
    case "chef": return <g><ellipse cx="50" cy="12" rx="22" ry="12" fill={c} /><rect x="32" y="16" width="36" height="20" rx="3" fill={c} /></g>;
    case "antenna": return <g><line x1="50" y1="22" x2="50" y2="4" stroke={c} strokeWidth="3" /><circle cx="50" cy="4" r="4.5" fill={c} /></g>;
    case "spikes": return <g fill={c}><path d="M26 34 L30 12 L38 30Z M40 28 L50 6 L60 28Z M62 30 L70 12 L74 34Z" /></g>;
    case "bandana": return <g><path d="M20 36 Q50 16 80 36 L72 42 Q50 28 28 42 Z" fill={c} /><circle cx="42" cy="32" r="1.6" fill="#fff" /><circle cx="56" cy="30" r="1.6" fill="#fff" /></g>;
    case "horns": return <g fill={c}><path d="M26 34 C14 28 12 14 16 6 C22 16 30 22 36 28Z" /><path d="M74 34 C86 28 88 14 84 6 C78 16 70 22 64 28Z" /></g>;
    case "helmet": return <g><path d="M20 40 Q50 -6 80 40 Z" fill={c} /><rect x="18" y="38" width="64" height="6" rx="3" fill={a} opacity=".6" /></g>;
    case "wizard": return <g><path d="M50 -8 L74 36 H26 Z" fill={c} /><rect x="20" y="34" width="60" height="7" rx="3.5" fill={c} /><circle cx="50" cy="18" r="3" fill={a} /><circle cx="42" cy="28" r="2" fill={a} /></g>;
    case "halo": return <ellipse cx="50" cy="10" rx="20" ry="5" fill="none" stroke={c} strokeWidth="4" />;
    case "crown": return <g><path d="M24 36 L26 12 L38 24 L50 8 L62 24 L74 12 L76 36Z" fill={c} stroke={a} strokeWidth="2" /><circle cx="50" cy="14" r="2.6" fill={a} /></g>;
    default: return null;
  }
}

function Accessory({ d }: { d: Record<string, any> }) {
  const c = d.color ?? "#0b1030";
  switch (d.kind) {
    case "glasses": return <g fill="none" stroke={c} strokeWidth="2.4"><circle cx="40" cy="46" r="8" /><circle cx="60" cy="46" r="8" /><line x1="48" y1="46" x2="52" y2="46" /></g>;
    case "monocle": return <g fill="none" stroke={c} strokeWidth="2.2"><circle cx="60" cy="46" r="8" /><path d="M62 54 Q66 66 62 74" /></g>;
    case "moustache": return <path d="M36 60 Q43 54 50 60 Q57 54 64 60 Q57 66 50 62 Q43 66 36 60Z" fill={c} />;
    case "mask": return <g><path d="M24 42 Q50 36 76 42 L72 54 Q50 50 28 54Z" fill={c} /><circle cx="40" cy="46" r="3" fill="#fff" /><circle cx="60" cy="46" r="3" fill="#fff" /></g>;
    case "headphones": return <g fill="none" stroke={c} strokeWidth="5" strokeLinecap="round"><path d="M22 52 V44 C22 24 78 24 78 44 V52" /><rect x="16" y="48" width="9" height="16" rx="4" fill={c} /><rect x="75" y="48" width="9" height="16" rx="4" fill={c} /></g>;
    case "scarf": return <g><rect x="30" y="68" width="40" height="9" rx="4" fill={c} /><rect x="58" y="72" width="9" height="20" rx="3" fill={c} /></g>;
    case "visor": return <rect x="26" y="38" width="48" height="14" rx="7" fill={c} opacity=".75" />;
    default: return null;
  }
}

export function Frame({ d, size }: { d?: Record<string, any>; size: number }) {
  if (!d) return null;
  const c = d.color ?? "#94a3b8";
  const w = Math.max(2, size / 22);
  const common = { x: w / 2, y: w / 2, width: size - w, height: size - w, rx: size * 0.2, fill: "none" } as const;
  switch (d.style) {
    case "pixel": return <rect {...common} stroke={c} strokeWidth={w * 1.4} strokeDasharray={`${w * 1.6} ${w * 1.1}`} />;
    case "neon": return <rect {...common} stroke={c} strokeWidth={w} style={{ filter: `drop-shadow(0 0 ${w * 1.5}px ${c})` }} />;
    case "gold": return <g><rect {...common} stroke={c} strokeWidth={w * 1.4} /><rect x={w * 1.6} y={w * 1.6} width={size - w * 3.2} height={size - w * 3.2} rx={size * 0.17} fill="none" stroke="#fff" strokeOpacity=".4" strokeWidth={w * 0.4} /></g>;
    case "flame": return <rect {...common} stroke={c} strokeWidth={w * 1.6} strokeDasharray={`${w * 3} ${w * 0.8}`} style={{ filter: `drop-shadow(0 0 ${w}px #ef4444)` }} />;
    case "crystal": return <rect {...common} stroke={c} strokeWidth={w * 1.3} strokeLinejoin="bevel" style={{ filter: `drop-shadow(0 0 ${w}px ${c})` }} />;
    case "double": return <g><rect {...common} stroke={c} strokeWidth={w} /><rect x={w * 2} y={w * 2} width={size - w * 4} height={size - w * 4} rx={size * 0.16} fill="none" stroke={c} strokeOpacity=".55" strokeWidth={w * 0.6} /></g>;
    case "electric": return <rect {...common} stroke={c} strokeWidth={w * 1.1} strokeDasharray={`${w * 0.6} ${w * 1.6} ${w * 2.4} ${w}`} style={{ filter: `drop-shadow(0 0 ${w * 1.4}px ${c})` }} />;
    case "vines": return <g><rect {...common} stroke="#166534" strokeWidth={w * 1.3} /><rect {...common} stroke={c} strokeWidth={w * 1.0} strokeDasharray={`${w * 0.5} ${w * 1.5}`} strokeLinecap="round" /></g>;
    case "rainbow":
      return (
        <g>
          <defs><linearGradient id="fr-rb" x1="0" y1="0" x2="1" y2="1">{["#ef4444", "#f59e0b", "#facc15", "#22c55e", "#38bdf8", "#a855f7"].map((col, i) => <stop key={i} offset={i / 5} stopColor={col} />)}</linearGradient></defs>
          <rect {...common} stroke="url(#fr-rb)" strokeWidth={w * 1.6} />
        </g>
      );
    case "royal":
      return (
        <g>
          <rect {...common} stroke={c} strokeWidth={w * 1.5} />
          <rect x={w * 1.7} y={w * 1.7} width={size - w * 3.4} height={size - w * 3.4} rx={size * 0.17} fill="none" stroke="#fff" strokeOpacity=".5" strokeWidth={w * 0.4} />
          {[[w * 1.4, w * 1.4], [size - w * 1.4, w * 1.4], [w * 1.4, size - w * 1.4], [size - w * 1.4, size - w * 1.4]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={w * 1.15} fill={["#ef4444", "#38bdf8", "#22c55e", "#a855f7"][i]} stroke="#fde68a" strokeWidth={w * 0.4} />)}
        </g>
      );
    case "galaxy":
      return (
        <g>
          <defs><linearGradient id="fr-gx" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#6366f1" /><stop offset=".5" stopColor="#d946ef" /><stop offset="1" stopColor="#22d3ee" /></linearGradient></defs>
          <rect {...common} stroke="url(#fr-gx)" strokeWidth={w * 1.6} style={{ filter: `drop-shadow(0 0 ${w * 1.3}px #a78bfa)` }} />
          {[[12, 4], [50, 2], [88, 5], [97, 40], [95, 80], [60, 97], [20, 96], [3, 60]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={w * 0.35} fill="#fff" />)}
        </g>
      );
    default: return <rect {...common} stroke={c} strokeWidth={w} />;
  }
}

export function Avatar({ equipped, size = 56, label, className, framing }: { equipped?: Equipped | null; size?: number; label?: string; className?: string; framing?: Framing }) {
  const e = equipped ?? {};
  const skin = e.skin?.data ?? { shape: "round", body: "#4cc9f0", accent: "#1b2a6b" };
  const shot = usePortrait(equipped, framing ?? (size < 64 ? "bust" : "full"), size);
  const bg = e.background?.data ?? { from: "#0b1030", to: "#312e81", pattern: "grid" };
  const gid = `g${(e.background?.id ?? "x").replace(/\W/g, "")}${size}`;
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={label ?? "Avatar"} style={{ display: "block", borderRadius: size * 0.2 }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={bg.from} />
          <stop offset="1" stopColor={bg.to} />
        </linearGradient>
        <clipPath id={`c${gid}`}>
          <rect width="100" height="100" rx="20" />
        </clipPath>
      </defs>
      <g clipPath={`url(#c${gid})`}>
        <rect width="100" height="100" fill={`url(#${gid})`} />
        <Pattern kind={bg.pattern} id={gid} />
        {shot ? (
          <image href={shot} x="0" y="0" width="100" height="100" preserveAspectRatio="xMidYMid slice" style={{ animation: "bb-fade .25s ease-out" }} />
        ) : (
          <>
            <Body shape={shapeOf(skin)} body={skin.body ?? "#4cc9f0"} accent={skin.accent ?? "#1b2a6b"} />
            {e.accessory && <Accessory d={e.accessory.data} />}
            {e.hat && <Hat d={e.hat.data} />}
          </>
        )}
      </g>
      <Frame d={e.frame?.data} size={100} />
    </svg>
  );
}

export function Banner({ item, children, height = 84 }: { item?: Item; children?: any; height?: number }) {
  const d = item?.data ?? { from: "#312e81", to: "#4cc9f0", pattern: "none" };
  return (
    <div className="banner" style={{ height }}>
      <svg viewBox="0 0 400 100" preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">
        <defs>
          <linearGradient id={`bn${item?.id ?? "d"}`} x1="0" x2="1">
            <stop offset="0" stopColor={d.from} />
            <stop offset="1" stopColor={d.to} />
          </linearGradient>
        </defs>
        <rect width="400" height="100" fill={`url(#bn${item?.id ?? "d"})`} />
        <g transform="scale(4 1)">
          <Pattern kind={d.pattern} id="b" />
        </g>
      </svg>
      <div className="banner-content">{children}</div>
    </div>
  );
}

/** Used in the shop preview: draws any cosmetic on top of the current look. */
export function previewEquipped(current: Equipped, item: { id: string; category: string; data: Record<string, any>; name: string }): Equipped {
  /* a hero is a complete look: it replaces every loose piece of the figure (the 2D things – background, frame – stay) */
  if (item.category === "hero") {
    const next: Equipped = { ...current };
    for (const slot of HERO_SLOTS) delete next[slot];
    return { ...next, hero: { id: item.id, data: item.data, name: item.name }, ...(heroLook(item) as Equipped) };
  }
  if (!["skin", "outfit", "hat", "accessory", "back", "hand", "pet", "aura", "emote", "background", "frame"].includes(item.category)) return current;
  const next: Equipped = { ...current, [item.category]: { id: item.id, data: item.data, name: item.name } };
  /* a new figure brings its own clothes */
  if (item.category === "skin") delete next.outfit;
  return next;
}
